// 1:1 port from age-of-mcp v3 (src/client/models/v3/refinery.ts) — the
// flattened part literals rebuilt from the original helpers + loops.
//
// v3 refinery — AUTHORED FOR THE 45° CAMERA. The still that cooks wood into
// truck fuel: a riveted steel boiler with dark HAZARD bands and a glowing
// FIRE box at its foot, a copper (terracotta-toned) condenser column with an
// arching pipe as the signature vertical, a dark chimney trailing pale
// smoke, and hazard-yellow (ROOF_LOGI) fuel barrels waiting in the open
// yard — the logistics colour code. The refiner stokes the fire-box in the
// open, never under a roof.

import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, CANVAS, FIRE, HAZARD, ROOF_HOME, ROOF_LOGI, STEEL } = styleV3;

const BASE = 0.12;            // foundation slab top

// The riveted boiler with its hazard bands and glowing fire-box mouth.
function boiler(): Part[] {
  return [
    cyl(0.19, 0.21, 0.4, 10, STEEL, [-0.07, BASE + 0.2, 0.03]),
    cyl(0.195, 0.195, 0.03, 10, HAZARD, [-0.07, BASE + 0.1, 0.03]),
    cyl(0.195, 0.195, 0.03, 10, HAZARD, [-0.07, BASE + 0.32, 0.03]),
    box(0.15, 0.1, 0.05, FIRE, [-0.07, BASE + 0.06, 0.22]),
  ];
}

// The copper condenser column, its cap and the pipe arching from the boiler.
function column(): Part[] {
  return [
    cyl(0.07, 0.08, 0.58, 8, ROOF_HOME, [0.18, BASE + 0.29, -0.06]),
    cyl(0.085, 0.085, 0.05, 8, HAZARD, [0.18, BASE + 0.58, -0.06]),
    cyl(0.025, 0.025, 0.28, 6, ROOF_HOME, [0.06, BASE + 0.45, -0.02], [0, 0, Math.PI / 2.4]),
  ];
}

// The dark chimney with its pale smoke puff.
function chimney(): Part[] {
  return [
    box(0.1, 0.44, 0.1, HAZARD, [-0.22, BASE + 0.34, -0.17]),
    box(0.12, 0.1, 0.12, CANVAS, [-0.22, BASE + 0.61, -0.17]),
  ];
}

// Hazard-yellow fuel barrels with dark bands, out in the yard.
function barrels(): Part[] {
  return [
    cyl(0.06, 0.06, 0.15, 7, ROOF_LOGI, [0.17, BASE + 0.075, 0.18]),
    cyl(0.063, 0.063, 0.03, 7, HAZARD, [0.17, BASE + 0.075, 0.18]),
    cyl(0.06, 0.06, 0.15, 7, ROOF_LOGI, [0.07, BASE + 0.075, 0.32], [0, 0, Math.PI / 2]),
  ];
}

// A refinery: a banded boiler still with copper column, chimney and fuel
// barrels in the open. Grows through the four build stages.
function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.5, 0.5)];
  if (s === 1) {
    return [
      slab(0.5, 0.5),
      cyl(0.19, 0.21, 0.14, 10, STEEL, [-0.07, BASE + 0.07, 0.03]),
      ...poles(0.44, 0.44, 0.5),
    ];
  }
  if (s === 2) return [slab(0.5, 0.5), ...boiler(), ...chimney(), ...poles(0.44, 0.44, 0.8)];
  return [slab(0.5, 0.5), ...boiler(), ...column(), ...chimney(), ...barrels()];
}

export const refineryV3 = defineModel({
  id: "refinery-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Refinery (v3)",
  tags: ["building","refinery","low-poly","age-of-mcp","v3"],
  summary: "Low-poly refinery.",
  description: "A still that cooks raw wood into fuel.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [-0.07, 0.12, 0.42], facing: Math.PI },
  ],
  interactions: [
    { id: "refine", label: "At the still", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
